import React from 'react';
import { Field, FieldProps } from 'react-final-form';
import {
  Date,
  DateNew,
  Time,
  Select,
  Input,
  FormMultiselectField,
  FormSingleSelect,
  Checkbox,
  Weekdays
} from 'components/Forms';
import { Radio } from '@material-ui/core';
import { DateTime } from 'luxon';
import { InputFieldProps, FormFieldType } from './utils';

interface FormFieldProps extends Partial<FieldProps<any, any>> {
  field: InputFieldProps;
  className?: string;
}

const dateTimeValidationFunc = (
  value: any,
  inputType: FormFieldType
): string | undefined => {
  if (DateTime.isDateTime(value) && !value.isValid) {
    return `This ${inputType} entry is invalid, please fix.`;
  }
  return undefined;
};

const FormField = ({ field, ...props }: FormFieldProps) => {
  if (!field.inputType) {
    return null;
  }

  return (
    <Field
      name={field.name}
      key={field.qaId}
      type={
        ['checkbox', 'radio'].includes(field.inputType)
          ? field.inputType
          : undefined
      }
      validate={value =>
        dateTimeValidationFunc(value, field.inputType as FormFieldType)
      }
      data-testid={field.qaId}
      {...props}
    >
      {({ input, meta }) => {
        if (field.inputType === 'select') {
          return <Select field={field} {...input} meta={meta} />;
        }

        if (field.inputType === 'multiselect' && field.optionRenderer) {
          return (
            <FormMultiselectField
              optionRenderer={field.optionRenderer}
              field={field}
              input={input}
              meta={meta}
            />
          );
        }

        if (field.inputType === 'singleselect') {
          return <FormSingleSelect field={field} />;
        }

        if (field.inputType === 'date') {
          return <Date field={field} {...input} meta={meta} />;
        }

        if (field.inputType === 'dateNew') {
          return <DateNew field={field} input={input} meta={meta} />;
        }

        if (field.inputType === 'time') {
          return <Time field={field} {...input} meta={meta} />;
        }

        if (field.inputType === 'checkbox') {
          return <Checkbox field={field} {...input} meta={meta} />;
        }

        if (field.inputType === 'weekdays') {
          return <Weekdays field={field} input={input} meta={meta} />;
        }

        if (field.inputType === 'textarea') {
          return (
            <Input field={field} {...input} meta={meta} multiline minRows={5} />
          );
        }

        // This doesn't output radio buttons correctly (they have no label and only one blank option)
        if (field.inputType === 'radio') {
          return <Radio {...input} />;
        }

        return <Input field={field} {...input} meta={meta} {...props} />;
      }}
    </Field>
  );
};

export default FormField;
